import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "@/packages/utils";
import { useChatPanel } from "./ChatPanelContext";
import type { ChatPanelProps } from "./types";

function OverlayChatPanel({
  children,
  className = "",
}: Omit<ChatPanelProps, "inline">) {
  const { t } = useTranslation(["common"]);
  const { isOpen, closePanel } = useChatPanel();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    function handlePointerDown(e: PointerEvent) {
      const target = e.target as Element;
      if (panelRef.current && !panelRef.current.contains(target)) {
        const isInRadixPortal = target.closest?.(
          "[data-radix-popper-content-wrapper]",
        );
        if (!isInRadixPortal) {
          closePanel();
        }
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen, closePanel]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Scrim overlay */}
          <motion.div
            key="chat-scrim"
            className="fixed inset-0 z-30 bg-black/5 backdrop-blur-[2px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
          <motion.div
            ref={panelRef}
            key="chat-panel"
            role="dialog"
            aria-label={t("ui.chatPanel.aiAssistant")}
            className={`chat-panel-container fixed top-3 right-3 bottom-3 w-[480px] max-w-[90vw] z-40 flex flex-col overflow-hidden ${className}`}
            initial={{ x: 40, opacity: 0, scale: 0.98 }}
            animate={{ x: 0, opacity: 1, scale: 1 }}
            exit={{ x: 40, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
          >
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export function ChatPanelRoot({
  children,
  className = "",
  inline,
}: ChatPanelProps) {
  if (inline) {
    return (
      <div
        className={`chat-panel-container flex flex-col h-full w-full overflow-hidden ${className}`}
      >
        {children}
      </div>
    );
  }

  return <OverlayChatPanel className={className}>{children}</OverlayChatPanel>;
}
